// // getConditions.js
// // Usage:
// //   import { getConditions, getLifeStage } from './getConditions';
// //   const { lifeStage, conditionGroups, allConditions } = getConditions(28, 'married');

import { BASE_URL } from "../../api/apiClient";

// import data from '../common/LifeStagesConditions.json';


// /**
//  * Marital status values that unlock fertility / pregnancy groups.
//  * Normalise whatever string you receive before passing in.
//  */
// const PARTNERED_STATUSES = ['married', 'partnered', 'in a relationship'];

// const normalizeStatus = (value) => String(value || '').toLowerCase().trim();

// const isPartneredStatus = (maritalStatus) =>
//   PARTNERED_STATUSES.includes(normalizeStatus(maritalStatus));

// export const isGroupVisibleForMaritalStatus = (group, maritalStatus = '') => {
//   if (!group.maritalStatusRequired) return true;

//   const isPartnered = isPartneredStatus(maritalStatus);
//   return group.maritalStatusRequired
//     .map(normalizeStatus)
//     .some((status) => PARTNERED_STATUSES.includes(status) && isPartnered);
// };

// /**
//  * Find the life stage object for a given age.
//  * If age falls in an overlap (e.g. 40–45 is both mature_reproductive
//  * and perimenopause), returns ALL matching stages.
//  */
// export const getLifeStages = (age) =>
//   data.lifeStages.filter(
//     (stage) => age >= stage.ageRange.min && age <= stage.ageRange.max,
//   );

// /**
//  * Main function.
//  *
//  * @param {number} age
//  * @param {string} maritalStatus  — e.g. 'married', 'single', 'partnered'
//  * @returns {{
//  *   lifeStages: object[],          // matched stage objects
//  *   conditionGroups: object[],     // filtered groups (marital gating applied)
//  *   allConditions: string[],       // flat deduplicated array of condition strings
//  * }}
//  */
// export const getConditions = (age, maritalStatus = '') => {
//   const normalizedMaritalStatus = normalizeStatus(maritalStatus);
//   const isPartnered = isPartneredStatus(normalizedMaritalStatus);
//   const matchedStages = getLifeStages(age);

//   if (!matchedStages.length) {
//     return { lifeStages: [], conditionGroups: [], allConditions: [] };
//   }

//   // Collect all condition groups across matched stages, apply marital gating
//   const conditionGroups = [];
//   const seen = new Set(); // deduplicate group names across overlapping stages

//   for (const stage of matchedStages) {
//     for (const group of stage.conditionGroups) {
//       // Skip fertility/pregnancy groups for non-partnered users
//       if (!isGroupVisibleForMaritalStatus(group, normalizedMaritalStatus)) {
//         continue;
//       }

//       // Avoid duplicating the same group from overlapping stages
//       const key = group.group;
//       if (!seen.has(key)) {
//         seen.add(key);
//         conditionGroups.push({ ...group, stageId: stage.id, stageLabel: stage.label });
//       }
//     }
//   }

//   // Flat deduplicated list of all condition strings
//   const allConditions = [
//     ...new Set(conditionGroups.flatMap((g) => g.conditions)),
//   ];

//   return { lifeStages: matchedStages, conditionGroups, allConditions };
// };

// /**
//  * Convenience: just get the flat conditions array.
//  */
// export const getConditionList = (age, maritalStatus) =>
//   getConditions(age, maritalStatus).allConditions; 
// getConditions.js
// Usage:
//   import { getConditions, getLifeStages, getAllLifeStages } from './getConditions';
//   const { lifeStages, conditionGroups, allConditions } = await getConditions(28, 'married');
//
// Everything here is async because the data comes from the API instead of
// a bundled JSON import. The shape of what you get back is unchanged from
// the original file-based version.

// Point this at wherever your API actually lives.
// If the patient app and the API share an origin, a relative path is fine.
// If they're on different domains, set this to the full origin instead,
// e.g. `${BASE_URL}/api/femcare/lifestages`.
const API_BASE = `${BASE_URL}/api/femcare/lifestages/`;

const PARTNERED_STATUSES = ["married", "partnered", "in a relationship"];

const normalizeStatus = (value) => String(value || "").toLowerCase().trim();

const isPartneredStatus = (maritalStatus) =>
  PARTNERED_STATUSES.includes(normalizeStatus(maritalStatus));

export const isGroupVisibleForMaritalStatus = (group, maritalStatus = "") => {
  if (!group.maritalStatusRequired) return true;

  const isPartnered = isPartneredStatus(maritalStatus);
  return group.maritalStatusRequired
    .map(normalizeStatus)
    .some((status) => PARTNERED_STATUSES.includes(status) && isPartnered);
};

/* ────────────────────────────────────────────────────────────────────────
 * FETCH + CACHE
 * The JSON rarely changes mid-session, so we fetch it once and reuse it.
 * `forceRefresh: true` (e.g. after the admin panel edits it) bypasses the
 * cache and hits the API again. All exported functions share this cache,
 * so calling getConditions() from one component and getAllLifeStages()
 * from another only ever triggers one network request.
 * ──────────────────────────────────────────────────────────────────── */

let cachedData = null;
let inflightRequest = null;

async function fetchLifeStagesData({ clinic, forceRefresh = false } = {}) {
  if (cachedData && !forceRefresh) return cachedData;
  if (inflightRequest && !forceRefresh) return inflightRequest;

  const url = clinic
    ? `${API_BASE}?clinic=${encodeURIComponent(clinic)}`
    : API_BASE;

  inflightRequest = fetch(url)
    .then((res) => {
      if (!res.ok) {
        throw new Error(`Failed to load life stages (${res.status})`);
      }
      return res.json();
    })
    .then((json) => {
      cachedData = json;
      inflightRequest = null;
      return json;
    })
    .catch((err) => {
      inflightRequest = null;
      throw err;
    });

  return inflightRequest;
}

/**
 * Returns the raw lifeStages array, unfiltered by age or marital status.
 * Useful when a component needs to build its own lookup maps
 * (e.g. HealthAssessments.jsx mapping every condition group to a route).
 *
 * @param {{ clinic?: string, forceRefresh?: boolean }} opts
 */
export const getAllLifeStages = async (opts = {}) => {
  const data = await fetchLifeStagesData(opts);
  return data.lifeStages;
};

/**
 * Find the life stage object(s) for a given age.
 * If age falls in an overlap (e.g. 40–45 is both mature_reproductive
 * and perimenopause), returns ALL matching stages.
 *
 * @param {number} age
 * @param {{ clinic?: string, forceRefresh?: boolean }} opts
 */
export const getLifeStages = async (age, opts = {}) => {
  const data = await fetchLifeStagesData(opts);
  return data.lifeStages.filter(
    (stage) => age >= stage.ageRange.min && age <= stage.ageRange.max,
  );
};

/**
 * Main function.
 *
 * @param {number} age
 * @param {string} maritalStatus  — e.g. 'married', 'single', 'partnered'
 * @param {{ clinic?: string, forceRefresh?: boolean }} opts
 * @returns {Promise<{
 *   lifeStages: object[],          // matched stage objects
 *   conditionGroups: object[],     // filtered groups (marital gating applied)
 *   allConditions: string[],       // flat deduplicated array of condition strings
 * }>}
 */
export const getConditions = async (age, maritalStatus = "", opts = {}) => {
  const normalizedMaritalStatus = normalizeStatus(maritalStatus);
  const data = await fetchLifeStagesData(opts);

  const matchedStages = data.lifeStages.filter(
    (stage) => age >= stage.ageRange.min && age <= stage.ageRange.max,
  );

  if (!matchedStages.length) {
    return { lifeStages: [], conditionGroups: [], allConditions: [] };
  }

  // Collect all condition groups across matched stages, apply marital gating
  const conditionGroups = [];
  const seen = new Set(); // deduplicate group names across overlapping stages

  for (const stage of matchedStages) {
    for (const group of stage.conditionGroups) {
      // Skip fertility/pregnancy groups for non-partnered users
      if (!isGroupVisibleForMaritalStatus(group, normalizedMaritalStatus)) {
        continue;
      }

      // Avoid duplicating the same group from overlapping stages
      const key = group.group;
      if (!seen.has(key)) {
        seen.add(key);
        conditionGroups.push({ ...group, stageId: stage.id, stageLabel: stage.label });
      }
    }
  }

  // Flat deduplicated list of all condition strings
  const allConditions = [
    ...new Set(conditionGroups.flatMap((g) => g.conditions)),
  ];

  return { lifeStages: matchedStages, conditionGroups, allConditions };
};

/**
 * Convenience: just get the flat conditions array.
 */
export const getConditionList = async (age, maritalStatus, opts) =>
  (await getConditions(age, maritalStatus, opts)).allConditions;

/**
 * Call this once after the admin panel saves an edit (or on a "refresh
 * content" action) to drop the cache and force the next call to refetch.
 */
export const invalidateLifeStagesCache = () => {
  cachedData = null;
  inflightRequest = null;
};